import { useState } from "owa-react";
import { getApplicationSettings } from "owa-application-settings";
import type { MailboxInfo } from "owa-client-types";
import { getBrowserWidth, isRunningInMetaOSHub } from "owa-config";
import { isAccountFeatureEnabled, isGlobalFeatureEnabled } from "owa-feature-flags";
import type { Features } from "owa-graph-schema";
import { getModuleContextMailboxInfo } from "owa-module-context-mailboxinfo";
import { isMsalEnabledForBusiness } from "owa-msaljs/lib/isMsalEnabledForBusiness";
import { canAccessManagementPage, checkIfCanAccessManagementPage, } from "places-management-permission";
import { isPlacesPremiumEnabled } from "places-settings";
import React from "react";
import { isMobileScreenSizeEnabled } from "./isMobileScreenSizeEnabled";

// Check if the user qualifies as an admin for space management.
function useIsSpaceManagementAdminTabEnabledInternal() {
    // As a first iteration admin is gated by a flight. We will implement rbacs checks later.
    const isMetaOS = isRunningInMetaOSHub();
    const isMSALEnabled = isMsalEnabledForBusiness();
    const isSpaceManagementFeatureEnabled = isGlobalFeatureEnabled("msplaces-space-management-page");
    const hasAdminAccess = isMetaOS
        ? isMSALEnabled && isSpaceManagementFeatureEnabled
        : isSpaceManagementFeatureEnabled;
    
    const windowWidth = getBrowserWidth();
    const mobileScreenSizeEnabled = isMobileScreenSizeEnabled(windowWidth);
    
    const isAdminEnabled = hasAdminAccess && mobileScreenSizeEnabled;
    
    const [checkingPermission, setCheckingPermission] = useState(false);
    const [checkFailed, setCheckFailed] = useState(false);
    
    const checkPermissionAsync = React.useCallback(async () => {
        setCheckingPermission(true);
        const result = await checkIfCanAccessManagementPage();
        setCheckingPermission(false);
        if (result == null) {
            setCheckFailed(true);
        }
    }, []);
    
    React.useEffect(() => {
        if (isAdminEnabled) {
            checkPermissionAsync();
        }
    }, [isAdminEnabled]);
    
    // Check if the user has the required permissions for space management
    const hasPermission = !!canAccessManagementPage();
    return {
        isManagementEnabled: isAdminEnabled,
        hasPermission: isAdminEnabled && hasPermission,
        isChecking: checkingPermission,
        checkFailed,
    };
}

export function useIsSpaceManagementAdminTabEnabled(): {
    isManagementEnabled: boolean;
    hasPermission: boolean;
    isChecking: boolean;
    checkFailed: boolean;
} {
    const mailboxInfo = getModuleContextMailboxInfo();
    const { isManagementEnabled, hasPermission: isManagementAdminTabEnabled, isChecking, checkFailed, } = useIsSpaceManagementAdminTabEnabledInternal();
    return {
        isManagementEnabled,
        hasPermission: devOverrideAdminEnabled(isManagementAdminTabEnabled, mailboxInfo),
        isChecking,
        checkFailed,
    };
}

// Checks if the user qualifies as an admin for space analytics
export function isSpaceAnalyticAdminTabEnabled(featureAccess: (Features | null)[] | undefined): boolean {
    const mailboxInfo = getModuleContextMailboxInfo();
    const isAdminFlightEnabled = getApplicationSettings("MsPlaces", mailboxInfo).enableAdminAvailableOptions;
    const hasAdminAccess: boolean = featureAccess?.some((access: Features | null) => access === "AllAdminFeatures") || false;
    const windowWidth = getBrowserWidth();
    const mobileScreenSizeEnabled = isMobileScreenSizeEnabled(windowWidth);
    
    const IsPlacesPremiumCheckDeprecated = isGlobalFeatureEnabled("msplaces-analytics-deprecate-premium-license-check");
    
    const isAdminEnabled = hasAdminAccess &&
        isAdminFlightEnabled &&
        (IsPlacesPremiumCheckDeprecated || isPlacesPremiumEnabled(mailboxInfo)) &&
        mobileScreenSizeEnabled;
    
    return devOverrideAdminEnabled(isAdminEnabled, mailboxInfo);
}

// Overrides the admin enabled state for dev purposes
function devOverrideAdminEnabled(isAdminEnabled: boolean, mailboxInfo: MailboxInfo) {
    return isAccountFeatureEnabled("places-dev-settings-admin-enabled", mailboxInfo)
        ? true
        : isAccountFeatureEnabled("places-dev-settings-admin-disabled", mailboxInfo)
            ? false
            : isAdminEnabled;
}
